import React, { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { useAccount } from "../contexts/AccountContext";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Send, Users, LogIn, LogOut, CheckCircle2, Pencil } from "lucide-react";
import TermsAcceptance from "../components/TermsAcceptance";
import SocialAuthButtons from "../components/SocialAuthButtons";
import SiteButton from "../components/SiteButton";

const COUNTRIES = [
  { v: "Portugal", pt: "Portugal", en: "Portugal" },
  { v: "Brazil", pt: "Brasil", en: "Brazil" },
  { v: "Spain", pt: "Espanha", en: "Spain" },
  { v: "France", pt: "França", en: "France" },
  { v: "United Kingdom", pt: "Reino Unido", en: "United Kingdom" },
  { v: "Angola", pt: "Angola", en: "Angola" },
  { v: "Mozambique", pt: "Moçambique", en: "Mozambique" },
  { v: "Cape Verde", pt: "Cabo Verde", en: "Cape Verde" },
  { v: "United States", pt: "Estados Unidos", en: "United States" },
  { v: "Other", pt: "Outro país", en: "Other country" },
];

export default function EnrollTeacher() {
  const { lang, settings } = useApp();
  const { account, logout } = useAccount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(`nm_enroll_cfg_${lang}`) || "null"); } catch { return null; }
  });
  const [form, setForm] = useState({
    quiz_id: "",
    teacher_name: searchParams.get("teacher") || "",
    teacher_email: searchParams.get("email") || "",
    teacher_email_confirm: searchParams.get("email") || "",
    school: searchParams.get("school") || "",
    class_name: searchParams.get("class") || "",
    country: "Portugal",
    num_students: 14,
  });
  const [extra, setExtra] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    const ck = `nm_enroll_cfg_${lang}`;
    try { const c = JSON.parse(sessionStorage.getItem(ck) || "null"); if (c) setConfig(c); } catch {}
    api.get(`/enrollment/config?lang=${lang}`).then((r) => {
      setConfig(r.data);
      try { sessionStorage.setItem(ck, JSON.stringify(r.data)); } catch {}
      const qs = r.data?.quizzes || [];
      if (qs.length > 0) setForm((f) => ({ ...f, quiz_id: f.quiz_id || qs[0].id }));
    }).catch(() => setConfig({ open: false, extra_fields: [], quizzes: [] }));
  }, [lang]);

  // Prefill from logged-in account (teacher/parent)
  useEffect(() => {
    if (account) {
      setForm((f) => ({
        ...f,
        teacher_name: f.teacher_name || account.name || "",
        teacher_email: f.teacher_email || account.email || "",
        teacher_email_confirm: f.teacher_email_confirm || account.email || "",
      }));
    }
  }, [account]);

  const tr = (pt, en) => (lang !== "en" ? pt : en);

  // Force validation messages to follow the selected language (not the browser locale).
  const valMsg = (el) => {
    const v = el.validity;
    if (v.valueMissing) return tr("Por favor, preencha este campo.", "Please fill in this field.");
    if (v.typeMismatch && el.type === "email") return tr("Por favor, introduza um email válido.", "Please enter a valid email address.");
    if (v.rangeUnderflow || v.rangeOverflow || v.badInput) return tr("Por favor, introduza um valor válido.", "Please enter a valid value.");
    return tr("Preenchimento inválido.", "Invalid input.");
  };
  const handleInvalid = (e) => { e.target.setCustomValidity(valMsg(e.target)); };
  const handleInput = (e) => { e.target.setCustomValidity(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.teacher_email.trim().toLowerCase() !== form.teacher_email_confirm.trim().toLowerCase()) {
      toast.error(tr("Os emails não coincidem. Verifique e tente novamente.", "The emails do not match. Please check and try again."));
      return;
    }
    if (!accepted) {
      toast.error(tr("Tem de aceitar os Termos e a Política de Privacidade.", "You must accept the Terms and Privacy Policy."));
      return;
    }
    // Show a review summary before the actual submission.
    setReviewing(true);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const { teacher_email_confirm, ...payload } = form;
      await api.post("/enrollment/submit", { ...payload, num_students: parseInt(form.num_students) || 1, extra, lang });
      // Point 3: show a confirmation message and close the form — no inline payment
      // options. The teacher pays later via the link in the confirmation email.
      setReviewing(false);
      setSubmitted(true);
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Erro ao submeter", "Submission error"));
    } finally {
      setSubmitting(false);
    }
  };

  const goHome = () => navigate("/");

  const inputCls = "w-full px-4 py-3 rounded-xl border-2 border-[#0B1D3A]/10 focus:border-[#005F73] outline-none font-nunito text-[#0B1D3A] transition-colors";

  if (config && !config.open) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-10 pb-20 text-center" data-testid="enroll-page">
        <h1 className="font-fredoka text-4xl md:text-5xl font-bold text-[#0B1D3A] mb-4">{tr("Inscrever Turma", "Enroll Class")}</h1>
        <div className="rounded-3xl bg-white p-8 border border-[#0B1D3A]/5 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.1)]">
          <p className="font-nunito text-lg text-[#0B1D3A]/70" data-testid="enroll-closed-message">
            {tr("As inscrições não estão abertas de momento. Volta em breve!", "Enrollments are not open right now. Check back soon!")}
          </p>
        </div>
      </div>
    );
  }

  const quizzes = config?.quizzes || [];

  const successMsg = config?.success_message || tr("Inscrição enviada! Verifique a sua caixa de correio.", "Enrollment sent! Please check your inbox.");
  const formTitle = config?.title || tr("Inscrever a Minha Turma", "Enroll My Class");
  const formSubtitle = config?.subtitle || tr("Este formulário apenas deve ser preenchido por professores.", "This registration form must be completed by a teacher.");

  const selectedQuiz = quizzes.find((q) => q.id === form.quiz_id);
  const selectedQuizName = selectedQuiz ? (lang === "en" ? selectedQuiz.name_en : (selectedQuiz.name_br || selectedQuiz.name_pt)) : "—";

  if (reviewing) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-12 pb-24" data-testid="enroll-review">
        <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#0B1D3A] mb-2 text-center">{tr("Confirmar inscrição", "Confirm enrollment")}</h1>
        <p className="text-center font-nunito text-[#0B1D3A]/60 mb-8">{tr("Reveja os dados antes de enviar.", "Review the details before sending.")}</p>
        <div className="rounded-3xl bg-white p-6 md:p-8 border border-[#0B1D3A]/5 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.1)] space-y-4">
          <div className="flex items-center justify-between gap-4 py-3 border-b border-[#0B1D3A]/8">
            <span className="font-fredoka font-semibold text-[#0B1D3A]/70">{tr("Concurso", "Contest")}</span>
            <span className="font-nunito font-bold text-[#0B1D3A] text-right" data-testid="enroll-review-quiz">{selectedQuizName}</span>
          </div>
          <div className="flex items-center justify-between gap-4 py-3">
            <span className="font-fredoka font-semibold text-[#0B1D3A]/70 flex items-center gap-1.5"><Users size={16} /> {tr("Nº de alunos", "Nr. of students")}</span>
            <span className="font-nunito font-bold text-[#0B1D3A] text-right" data-testid="enroll-review-students">{parseInt(form.num_students) || 1}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <SiteButton onClick={doSubmit} disabled={submitting} color="#1B5E3A" icon={CheckCircle2} testId="enroll-confirm-btn">
              {submitting ? tr("A enviar...", "Sending...") : tr("Confirmar", "Confirm")}
            </SiteButton>
            <SiteButton onClick={() => setReviewing(false)} disabled={submitting} color="#94A3B8" icon={Pencil} testId="enroll-edit-btn">
              {tr("Alterar", "Edit")}
            </SiteButton>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-16 pb-24 text-center" data-testid="enroll-success">
        <div className="rounded-3xl bg-white p-10 border border-[#0B1D3A]/5 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.12)]">
          <div className="mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#2D6A4F]/10">
            <CheckCircle2 size={40} className="text-[#2D6A4F]" />
          </div>
          <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#0B1D3A] mb-3" data-testid="enroll-success-message">{successMsg}</h1>
          <SiteButton onClick={goHome} color="#1B5E3A" testId="enroll-success-home-btn">
            {tr("Voltar ao início", "Back to home")}
          </SiteButton>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pt-10 pb-20" data-testid="enroll-page">
      <h1 className="font-fredoka text-4xl md:text-5xl font-bold text-[#0B1D3A] mb-2 text-center" data-testid="enroll-form-title">{formTitle}</h1>
      <p className="text-center font-nunito text-[#0B1D3A]/60 mb-8" data-testid="enroll-form-subtitle">{formSubtitle}</p>

      <form onSubmit={submit} className="rounded-3xl bg-white p-6 md:p-8 border border-[#0B1D3A]/5 shadow-[0_12px_40px_-10px_rgba(11,29,58,0.1)] space-y-4">
        <div>
          <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("Concurso", "Contest")} *</label>
          <select required value={form.quiz_id} onChange={(e) => setForm({ ...form, quiz_id: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} className={inputCls} data-testid="enroll-quiz-select">
            {quizzes.length === 0 && <option value="">{config === null ? tr("A carregar concursos...", "Loading contests...") : tr("Sem concursos disponíveis", "No contests available")}</option>}
            {quizzes.map((qz) => (
              <option key={qz.id} value={qz.id}>{lang === "en" ? qz.name_en : (qz.name_br || qz.name_pt)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("Nome do professor", "Teacher's name")} *</label>
          <input required value={form.teacher_name} onChange={(e) => setForm({ ...form, teacher_name: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} className={inputCls} data-testid="enroll-teacher-name" />
        </div>
        <div>
          <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("Email do professor", "Teacher's email")} *</label>
          <input required type="email" value={form.teacher_email} onChange={(e) => setForm({ ...form, teacher_email: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} className={inputCls} data-testid="enroll-teacher-email" />
        </div>
        <div>
          <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("Confirmar email do professor", "Confirm teacher's email")} *</label>
          <input required type="email" value={form.teacher_email_confirm} onChange={(e) => setForm({ ...form, teacher_email_confirm: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} onPaste={(e) => e.preventDefault()} className={inputCls} data-testid="enroll-teacher-email-confirm" />
          {form.teacher_email_confirm && form.teacher_email.trim().toLowerCase() !== form.teacher_email_confirm.trim().toLowerCase() && (
            <p className="text-xs text-[#FF6B6B] font-nunito mt-1" data-testid="enroll-email-mismatch">{tr("Os emails não coincidem.", "The emails do not match.")}</p>
          )}
        </div>
        <div>
          <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("Escola", "School")} *</label>
          <input required value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} className={inputCls} data-testid="enroll-school" />
        </div>
        <div>
          <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("País", "Country")} *</label>
          <select required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} className={inputCls} data-testid="enroll-country-select">
            {COUNTRIES.map((c) => (
              <option key={c.v} value={c.v}>{lang !== "en" ? c.pt : c.en}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{tr("Nome da turma", "Class name")} *</label>
            <input required value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} placeholder={tr("ex.: 5.º A, Turma 3", "e.g.: 5A, Group 3")} className={inputCls} data-testid="enroll-class-name" />
          </div>
          <div>
            <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5 flex items-center gap-1"><Users size={16} /> {tr("Nº de alunos", "Nr. of students")} *</label>
            <input required type="number" min="1" max="300" value={form.num_students} onChange={(e) => setForm({ ...form, num_students: e.target.value })} onInvalid={handleInvalid} onInput={handleInput} className={inputCls} data-testid="enroll-num-students" />
          </div>
        </div>

        {(config?.extra_fields || []).map((f) => (
          <div key={f.key}>
            <label className="block font-fredoka font-semibold text-[#0B1D3A] mb-1.5">{lang === "en" ? (f.label_en || f.label_pt) : (lang === "br" ? (f.label_br || f.label_pt) : f.label_pt)}{f.required ? " *" : ""}</label>
            {f.type === "textarea" ? (
              <textarea required={f.required} rows="3" value={extra[f.key] || ""} onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })} className={inputCls} data-testid={`enroll-extra-${f.key}`} />
            ) : (
              <input required={f.required} type={f.type === "email" ? "email" : f.type === "number" ? "number" : "text"} value={extra[f.key] || ""} onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })} className={inputCls} data-testid={`enroll-extra-${f.key}`} />
            )}
          </div>
        ))}

        <div className="pt-1">
          <TermsAcceptance checked={accepted} onChange={setAccepted} lang={lang} testId="enroll-terms" />
        </div>

        <SiteButton type="submit" disabled={submitting} color="#1B5E3A" icon={Send} testId="enroll-submit-btn">
          {submitting ? tr("A submeter...", "Submitting...") : tr("Inscrever turma", "Enroll class")}
        </SiteButton>

        {account ? (
          <div className="rounded-2xl bg-[#2D6A4F]/[0.07] border border-[#2D6A4F]/20 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="enroll-logged-in">
            <span className="font-nunito text-sm text-[#0B1D3A] flex items-center gap-2">
              <CheckCircle2 size={18} className="text-[#2D6A4F] shrink-0" />
              <span>{tr("Sessão iniciada como", "Signed in as")} <strong>{account.name || account.email}</strong>. {tr("Os seus dados foram preenchidos.", "Your details were filled in.")}</span>
            </span>
            <button type="button" onClick={() => { logout(); setForm((f) => ({ ...f, teacher_name: "", teacher_email: "" })); }}
              data-testid="enroll-logout-btn"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1D3A]/60 hover:text-[#FF6B6B] transition-colors shrink-0">
              <LogOut size={15} /> {tr("Terminar sessão", "Sign out")}
            </button>
          </div>
        ) : (
          (settings?.account_email_enabled !== false ||
            (!!process.env.REACT_APP_GOOGLE_CLIENT_ID && settings?.account_google_enabled !== false) ||
            (!!process.env.REACT_APP_FACEBOOK_APP_ID && settings?.account_facebook_enabled !== false)) ? (
          <div className="rounded-2xl border border-[#0B1D3A]/10 p-4" data-testid="enroll-login-panel">
            <p className="font-fredoka font-semibold text-[#0B1D3A] mb-1">{tr("Já tem conta? Entre para preencher mais rápido", "Have an account? Sign in to fill faster")}</p>
            <p className="text-xs text-[#0B1D3A]/55 font-nunito mb-3">{tr("Opcional — também pode inscrever-se sem conta.", "Optional — you can also enroll without an account.")}</p>
            <SocialAuthButtons redirectTo={null} />
            {settings?.account_email_enabled !== false && (
              <Link to="/account" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#005F73] hover:underline" data-testid="enroll-email-login-link">
                <LogIn size={15} /> {tr("Entrar com email e palavra-passe", "Sign in with email & password")}
              </Link>
            )}
          </div>
          ) : null
        )}
      </form>
    </div>
  );
}
